import { useState, useCallback } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { attachmentApi } from '../api/attachmentApi';
import type {
  AttachmentUploadResponse,
  AttachmentPage,
  AttachmentPreviewMeta,
  AttachmentApiError,
} from '../types/attachment';

// ─── Query Keys (consistent pattern) ─────────────────────────────────────────

export const attachmentKeys = {
  all: ['attachment-demo'] as const,
  lists: () => [...attachmentKeys.all, 'list'] as const,
  list: (page: number, size: number) =>
    [...attachmentKeys.lists(), { page, size }] as const,
  preview: (fileId: string) =>
    [...attachmentKeys.all, 'preview', fileId] as const,
};

// ─── useMyAttachments ─────────────────────────────────────────────────────────

export function useMyAttachments(page = 0, size = 20) {
  return useQuery<AttachmentPage, AttachmentApiError>({
    queryKey: attachmentKeys.list(page, size),
    queryFn: () => attachmentApi.listMine(page, size),
    staleTime: 30_000,
  });
}

// ─── useUploadAttachment ──────────────────────────────────────────────────────

export function useUploadAttachment() {
  const queryClient = useQueryClient();
  const [uploadProgress, setUploadProgress] = useState(0);

  const mutation = useMutation<
    AttachmentUploadResponse,
    AttachmentApiError,
    File
  >({
    mutationFn: (file: File) =>
      attachmentApi.upload(file, (pct) => setUploadProgress(pct)),
    onSuccess: () => {
      setUploadProgress(0);
      // Invalidate list so the new row appears
      void queryClient.invalidateQueries({ queryKey: attachmentKeys.lists() });
    },
    onError: () => {
      setUploadProgress(0);
    },
  });

  const reset = useCallback(() => {
    setUploadProgress(0);
    mutation.reset();
  }, [mutation]);

  return { ...mutation, uploadProgress, reset };
}

// ─── useDeleteAttachment ──────────────────────────────────────────────────────

export function useDeleteAttachment() {
  const queryClient = useQueryClient();

  return useMutation<void, AttachmentApiError, string>({
    mutationFn: (fileId: string) => attachmentApi.deleteOne(fileId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: attachmentKeys.lists() });
    },
  });
}

// ─── usePreviewAttachment ─────────────────────────────────────────────────────

export function usePreviewAttachment(fileId: string | null) {
  return useQuery<AttachmentPreviewMeta, AttachmentApiError>({
    queryKey: attachmentKeys.preview(fileId ?? ''),
    queryFn: () => attachmentApi.getPreview(fileId!),
    enabled: fileId !== null,
    staleTime: 5 * 60 * 1000, // 5 min (matches presignTtl)
    retry: false,
  });
}

// ─── useDownloadAttachment ────────────────────────────────────────────────────

/**
 * Triggers a browser download via fetch + Blob + createObjectURL.
 * The JWT is sent as an Authorization header so the backend's
 * JwtAuthenticationFilter can authenticate the download request.
 * Returns isDownloading state and a download error for optional UI feedback.
 */
export function useDownloadAttachment() {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<AttachmentApiError | null>(null);

  const download = useCallback(async (fileId: string, originalName: string) => {
    setIsDownloading(true);
    setDownloadError(null);
    try {
      await attachmentApi.download(fileId, originalName);
    } catch (err) {
      setDownloadError(err as AttachmentApiError);
    } finally {
      setIsDownloading(false);
    }
  }, []);

  return { download, isDownloading, downloadError };
}
